/**
 * GrantChargeDate
 *
 * "Charged on 8 September 2026", shown next to the TOTAL when a promotional grant defers the
 * first payment.
 *
 * This deliberately duplicates what {@link GrantCoverageNotice} already states higher up. The
 * amount is what a member checks before committing, and next to it the total is the one number
 * that looks like a charge due now — so the qualification has to travel with it. A member who
 * skims past the notice, or collapses it, must not be able to reach the pay button believing
 * the total is coming out today.
 *
 * Renders nothing when the payment is NOT deferred — including the credited case, where the
 * total genuinely is charged today and a date would be a lie.
 */

import type { MembershipPlanChange } from "@/types/cart";
import { useLocalizedTexts, t } from "@/utils/translations";
import { formatDate } from "@/utils/entitlements";
import styles from "./GrantChargeDate.module.css";

interface GrantChargeDateProps {
  /** The cart's membership change; the line renders only when a first payment is deferred. */
  membershipChange: MembershipPlanChange | null | undefined;
  className?: string;
}

export function GrantChargeDate({
  membershipChange,
  className,
}: GrantChargeDateProps) {
  const localizedTexts = useLocalizedTexts();

  const trialEnd = membershipChange?.grantTrialEndUtc;
  if (!trialEnd) {
    return null;
  }

  return (
    <p
      className={
        className ? `${styles.chargeDate} ${className}` : styles.chargeDate
      }
    >
      {t(
        localizedTexts,
        "Tracker.Cart.GrantCoverage.ChargedOn",
        "Charged on {date}"
      ).replace("{date}", formatDate(trialEnd))}
    </p>
  );
}
